// The quickmatch waiting room (D217).
//
// It used to be one line of text — "finding a game… (#2 in queue)" — over a Cancel button,
// which told a player nothing about whether the wait was working, how long it would last,
// or whether it would ever end. At this population it usually would not: a lone player
// waited forever behind a queue that had nobody in it.
//
// Now the queue answers three questions and this renders them: how much of the table is
// here, how hard the search is trying, and when it stops waiting. `queueCopy` is pure, so
// every one of those states is a unit test rather than a thing you have to stand in front
// of the real server to see — main.ts is coverage-excluded, which is exactly why this does
// not live there any more.

export type QueueUiState =
  | { kind: 'joining' }
  | { kind: 'waiting'; found: number; needed: number; elapsedMs: number; widening: boolean; backfillInMs: number | null }
  | { kind: 'formed'; backfill: boolean }
  | { kind: 'unavailable' };

export interface QueueCopy {
  /** The headline. */
  title: string;
  /** The honest second line, or '' when there is nothing true to add. */
  detail: string;
}

const secs = (ms: number): number => Math.max(0, Math.round(ms / 1000));

/**
 * What the overlay says. Pure, and deliberately never optimistic: it reports the pool it
 * actually has, and when the wait will end it says so in seconds rather than implying an
 * opponent is moments away.
 */
export function queueCopy(s: QueueUiState): QueueCopy {
  switch (s.kind) {
    case 'joining':
      return { title: 'Finding a game…', detail: '' };
    case 'waiting': {
      const title = `Finding a game… ${s.found} of ${s.needed}`;
      if (s.backfillInMs !== null && s.backfillInMs <= 0) return { title, detail: 'Bringing in bots to fill the table…' };
      const bits: string[] = [`${secs(s.elapsedMs)}s`];
      if (s.widening) bits.push('widening the search');
      if (s.backfillInMs !== null) bits.push(`bots in ${secs(s.backfillInMs)}s`);
      return { title, detail: bits.join(' · ') };
    }
    case 'formed':
      // D288: a bot seat no longer un-ranks anything — two humans do or do not rate a game
      return { title: 'Table found!', detail: s.backfill ? 'Bots filled the empty seats — it is ranked if another human is at the table.' : '' };
    case 'unavailable':
      return { title: 'Quickmatch is unavailable right now', detail: 'Try Solo vs bots, or create a room and share the link.' };
  }
}

export interface QueueOverlayOpts {
  doc: Document;
  onCancel(): void;
}

export interface QueueOverlay {
  readonly root: HTMLElement;
  set(state: QueueUiState): void;
  dispose(): void;
}

export function mountQueueOverlay(opts: QueueOverlayOpts): QueueOverlay {
  const d = opts.doc;
  const root = d.createElement('div');
  root.className = 'net-overlay';
  root.dataset['quickmatchOverlay'] = '1';

  const title = d.createElement('div');
  title.className = 'queue-title';
  title.dataset['queueTitle'] = '1';
  const detail = d.createElement('div');
  detail.className = 'queue-detail';
  detail.dataset['queueDetail'] = '1';

  const cancel = d.createElement('button');
  cancel.type = 'button';
  cancel.className = 'menu-btn menu-btn--create';
  cancel.dataset['quickmatchCancel'] = '1';
  cancel.textContent = 'Cancel';
  cancel.addEventListener('click', () => opts.onCancel());

  // The recorded bug this shape exists to prevent: the old overlay wrote its error by
  // reassigning the CONTAINER's textContent, which destroyed the Cancel button — leaving a
  // player who hit a failing enqueue with no way out but a reload. Separate nodes, always.
  root.append(title, detail, cancel);
  d.body.appendChild(root);

  return {
    root,
    set(state: QueueUiState): void {
      const copy = queueCopy(state);
      title.textContent = copy.title;
      detail.textContent = copy.detail;
    },
    dispose(): void {
      root.remove();
    },
  };
}
